import { isSuccess } from '@/context/services/type';
import { isCommonError } from '@/libs/typeguard/isError';
import {
  ApprovingInvoiceReportDisapprovalRequest,
  useCreateApprovingInvoiceReportDisapproval,
  useGetApprovingInvoiceReportDisapprovalRouteSuspense,
} from 'ap-openapi';
import { useCallback, useMemo } from 'react';
import type {
  FetchApprovingInvoiceReportDisapprovalRouteArgs,
  FetchApprovingInvoiceReportDisapprovalRouteResult,
} from './type';

export function useGetApprovingInvoiceReportDisapprovalRoute(
  args: FetchApprovingInvoiceReportDisapprovalRouteArgs
): FetchApprovingInvoiceReportDisapprovalRouteResult {
  const { reportId } = args;

  const { data, error } = useGetApprovingInvoiceReportDisapprovalRouteSuspense(
    reportId,
    {
      query: {
        queryKey: ['approvingInvoiceReports', reportId],
      },
    }
  );

  if (error) {
    throw error;
  }
  const body = data?.data;
  const status = data?.status;
  const isSuccessStatus = isSuccess(status);

  return useMemo(() => {
    if (!isSuccessStatus) {
      if (typeof body === 'object' && isCommonError(body)) {
        return {
          data: undefined,
          error: {
            messages: body?.messages,
            code: status?.toString(),
          },
        };
      }
      return {
        data: undefined,
        error: {
          messages: undefined,
          code: status?.toString(),
        },
      };
    }
    return {
      data: body,
      error: undefined,
    };
  }, [body, isSuccessStatus, status]);
}

export const useCreateApprovingInvoiceReportDisapprovalMutation = () => {
  const { mutateAsync: _mutateAsync } =
    useCreateApprovingInvoiceReportDisapproval();

  const mutateAsync = useCallback(
    async (args: {
      id: string;
      data?: ApprovingInvoiceReportDisapprovalRequest;
    }) => {
      try {
        const resp = await _mutateAsync({ id: args.id, data: args.data ?? {} });
        if (isSuccess(resp.status)) {
          return {
            status: 'success' as const,
            data: resp.data,
          };
        }
        return {
          status: 'error' as const,
          errors: {
            code: resp.status.toString(),
            messages:
              typeof resp?.data === 'object' && isCommonError(resp?.data)
                ? resp?.data?.messages
                : undefined,
          },
        };
      } catch (error) {
        return {
          status: 'error',
          errors: { messages: undefined },
        };
      }
    },
    [_mutateAsync]
  );

  return { mutateAsync };
};
